import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';

// Iter 84 · module-level cache so the same evidence photo isn't re-fetched
// (and re-authenticated) every time its <AuthImage> remounts — e.g. React
// StrictMode's double-invoke in dev, or the same photo shown in 2 places.
const _blobCache = new Map();

/**
 * AuthImage — renders an <img> for evidence photos stored behind
 * GET /api/files/{path}, which requires an Authorization header (plain
 * <img src> can't send one). Fetches the blob via the authenticated `api`
 * client and renders it via a local object URL. Accepts data:/blob: URLs
 * (legacy base64 photos, or freshly-selected local previews) as-is.
 */
export default function AuthImage({ src, alt = 'evidencia', className, style, ...rest }) {
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        if (!src) { setBlobUrl(null); return; }
        if (src.startsWith('data:') || src.startsWith('blob:')) { setBlobUrl(src); return; }
        let cancelled = false;
        // Iter 85 · cachear la PROMESA (no solo la URL ya resuelta) — así dos
        // montajes casi simultáneos (p. ej. React StrictMode) para la misma
        // foto reutilizan la misma solicitud en vuelo, en vez de disparar 2
        // GET /api/files/... concurrentes.
        if (!_blobCache.has(src)) {
            const p = api.get(src.replace(/^\/api/, ''), { responseType: 'blob' }).then((r) => URL.createObjectURL(r.data));
            _blobCache.set(src, p);
        }
        _blobCache.get(src).then((u) => { if (!cancelled) setBlobUrl(u); }).catch(() => {
            _blobCache.delete(src);
            if (!cancelled) setBlobUrl(null);
        });
        return () => { cancelled = true; };
    }, [src]);

    if (!blobUrl) {
        return (
            <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.05)' }} {...rest}>
                <Loader2 className="w-4 h-4 animate-spin opacity-50" />
            </div>
        );
    }
    return <img src={blobUrl} alt={alt} className={className} style={style} {...rest} />;
}
